// src/pages/reset-password/index.jsx
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { callFunctionRaw } from "@/lib/callFunction.js";
import AccountPasswordLifecyclePanel from "@/pages/profile-settings/AccountPasswordLifecyclePanel.jsx";

export default function ResetPasswordPage() {
  const nav = useNavigate();
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const token = String(params.get("token") || "").trim();
  const [success, setSuccess] = React.useState(false);

  async function handleResetPassword({ password }) {
    if (!token) {
      throw new Error(
        t(
          "auth.resetPassword.invalidLink",
          "This link is invalid or expired. Please request a new one.",
        ),
      );
    }

    try {
      await callFunctionRaw(
        "account-password-reset",
        { action: "confirm", token, password, resetBasePath: "/reset-password" },
        { timeoutMs: 20000, skipAuth: true },
      );
      setSuccess(true);
    } catch {
      throw new Error(
        t(
          "auth.resetPassword.invalidLink",
          "This link is invalid or expired. Please request a new one.",
        ),
      );
    }
  }

  if (success) {
    return (
      <div className="auth-bg min-h-[100svh] grid place-items-center px-4">
        <div className="w-full max-w-[520px] text-center">
          <img
            src="/assets/images/afrimed_logo_stack.svg"
            alt={t("auth.resetPassword.successTitle", "Password reset")}
            className="mx-auto mb-6 h-auto w-[120px]"
            draggable="false"
          />
          <h1 className="mb-2 text-title-large text-on-surface">
            {t("auth.resetPassword.successTitle", "Password reset")}
          </h1>
          <p className="mb-6 text-body-medium text-on-surface-var">
            <span className="block">
              {t("auth.resetPassword.successIntroLine1", "Your password has been updated.")}
            </span>
            <span className="block">
              {t(
                "auth.resetPassword.successIntroLine2",
                "You can now log in with your email and password.",
              )}
            </span>
          </p>
          <button
            type="button"
            onClick={() => nav("/login", { replace: true })}
            className="h-9 rounded-lg px-3 text-body-medium font-medium"
            style={{
              background: "rgba(29,79,215,0.10)",
              color: "rgb(var(--afrimed-primary))",
              boxShadow: "0 0 0 1px rgba(29,79,215,0.18) inset",
            }}
          >
            {t("auth.resetPassword.backToLogin", "Back to login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-bg min-h-[100svh] grid place-items-center px-4">
      <div className="w-full max-w-[520px] text-center">
        <img
          src="/assets/images/afrimed_logo_stack.svg"
          alt={t("auth.resetPassword.publicTitle", "Reset your password")}
          className="mx-auto mb-6 h-auto w-[120px]"
          draggable="false"
        />

        <AccountPasswordLifecyclePanel
          className="text-left"
          intent="create"
          hasPasswordOverride={false}
          titleOverride={t("auth.resetPassword.publicTitle", "Reset your password")}
          copyOverride={null}
          createSignOutNoticeOverride={null}
          titleGroupClassName="text-center"
          titleClassName="text-title-large text-on-surface"
          requirementsPresentation="sentence"
          requirementsRailClassName="mx-auto w-full max-w-[360px]"
          submitLabelOverride={t("auth.resetPassword.resetAction", "Reset password")}
          canSubmitExtra={Boolean(token)}
          onPasswordSubmit={handleResetPassword}
          enableResetHandoff={false}
          trailingFormContent={
            !token ? (
              <div className="flex items-center justify-center gap-6 text-center">
                <button
                  type="button"
                  onClick={() => nav("/forgot-password", { replace: true })}
                  className="font-medium text-[rgb(var(--afrimed-primary))] hover:opacity-90"
                >
                  {t("auth.resetPassword.requestNewLink", "Request new reset link")}
                </button>
                <button
                  type="button"
                  onClick={() => nav("/login", { replace: true })}
                  className="font-medium text-[rgb(var(--afrimed-primary))] hover:opacity-90"
                >
                  {t("auth.resetPassword.backToLogin", "Back to login")}
                </button>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
}
